import { useMemo } from "react";
import type { LatLngTuple } from "leaflet";
import { useQuery } from "@tanstack/react-query";

import { referencesAjax } from "../../ajax/ReferencesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { MINIMUM_VERTICES, parseGeofencePolygon } from "./geofencePolygon";

/**
 * The one place the geofences are fetched and parsed.
 *
 * Note: three screens need this list - the machine map draws it, the machine map also needs its EXTENT to
 * pick an opening viewport, and the geofence editor shows the neighbouring fences so an admin can draw
 * against them. Sharing one query key means the second and third readers cost no extra request.
 */

// Note: its own key rather than sharing ['references','locations'] with the Operations screen. That one
// stores options mapped for a dropdown; two different shapes under one key is a cache collision that
// would surface as a type error at best and a blank dropdown at worst.
export const GEOFENCE_QUERY_KEY = ["references", "locations", "geofences"];

// Note: an explicit union rather than letting flatMap infer it. The two branches return different shapes,
// and inference widened them into something React could not narrow on `kind` at all.
export type Fence =
  | { kind: "circle"; id: number; name: string; buffer: number; centre: LatLngTuple; radius: number; locationTypeId: number; stockpileType: string | null }
  | { kind: "polygon"; id: number; name: string; buffer: number; positions: LatLngTuple[]; locationTypeId: number; stockpileType: string | null };

export function useGeofences({ enabled = true }: { enabled?: boolean } = {}): { fences: Fence[]; isLoading: boolean } {
  const { data: locations = [], isLoading } = useQuery({
    queryKey: GEOFENCE_QUERY_KEY,
    queryFn: async () => {
      const res = await referencesAjax.getLocations({ request: { pageNumber: 1, pageSize: 1000 } });
      if (ErrorResponse.isErrorResponse(res) || !res.data) return [] as LocationDTO[];
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const fences = useMemo<Fence[]>(() => locations.flatMap((location): Fence[] => {
    const buffer = location.geofenceAccuracyBufferMetres ?? 0;

    if (location.geofencePolygon) {
      const { vertices } = parseGeofencePolygon(location.geofencePolygon);

      // Note: a polygon that will not parse is skipped rather than drawn wrong. The server logs it and
      // falls back to the circle for matching, so drawing the circle here is the honest picture of what
      // the machine is actually being matched against.
      if (vertices.length >= MINIMUM_VERTICES)
        return [{
          kind: "polygon" as const,
          id: location.id,
          name: location.name,
          buffer,
          positions: vertices.map(v => [v.lat, v.lng] as LatLngTuple),
          locationTypeId: location.locationTypeId,
          stockpileType: location.stockpileType ?? null,
        }];
    }

    if (location.centerLatitude != null && location.centerLongitude != null && location.geofenceRadiusMetres != null)
      return [{
        kind: "circle" as const,
        id: location.id,
        name: location.name,
        buffer,
        centre: [location.centerLatitude, location.centerLongitude] as LatLngTuple,
        radius: location.geofenceRadiusMetres,
        locationTypeId: location.locationTypeId,
        stockpileType: location.stockpileType ?? null,
      }];

    return [];
  }), [locations]);

  return { fences, isLoading };
}

/**
 * Every corner of every fence, for fitting a viewport.
 *
 * Note: a circle contributes its centre offset by roughly its own radius rather than just the centre,
 * so a fence is not half off the edge of the opening view. 111,320 m per degree of latitude, narrowed by
 * the cosine of the latitude for longitude - flat-earth, which is accurate well past the scale of a mine.
 */
export function fenceCorners(fences: Fence[]): LatLngTuple[] {
  return fences.flatMap((fence): LatLngTuple[] => {
    if (fence.kind === "polygon") return fence.positions;

    const [lat, lng] = fence.centre;
    const dLat = (fence.radius + fence.buffer) / 111_320;
    const dLng = dLat / Math.max(Math.cos((lat * Math.PI) / 180), 1e-6);

    return [[lat - dLat, lng - dLng], [lat + dLat, lng + dLng]];
  });
}
